import React from 'react';
import { User, Membership } from '../types';
import { modules, lessons } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';

interface ModulesPageProps {
  navigate: (view: string, slug: string) => void;
  user: User | null;
}

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
    </svg>
);


const ModulesPage: React.FC<ModulesPageProps> = ({ navigate, user }) => {
  const { t } = useLanguage();

  return (
    <div className="container mx-auto max-w-4xl">
      <h1 className="text-4xl font-bold mb-8 text-center">{t('modulesPage.title')}</h1>
      <div className="space-y-8">
        {modules.sort((a,b) => a.order - b.order).map(module => {
          const isLocked = module.is_premium && user?.membership !== Membership.Premium;
          const moduleLessons = lessons.filter(l => l.module === module.slug);

          return (
            <div key={module.slug} className="bg-white/60 p-8 rounded-4xl shadow-lg border border-white/50">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    {isLocked && <LockIcon />}
                    <h2 className="text-3xl font-bold">{module.title}</h2>
                  </div>
                  <span className="text-sm font-medium bg-accent/20 text-accent px-3 py-1 rounded-full">{module.level}</span>
                </div>
                {isLocked && 
                    <button 
                        onClick={() => navigate('profile', '')}
                        className="text-sm font-semibold text-accent hover:underline"
                    >
                        {t('modulesPage.upgrade')}
                    </button>
                }
              </div>
              <p className="text-stone-600 mb-6">{module.summary}</p>
              
              <div className="space-y-3">
                <h3 className="font-semibold text-stone-700">{t('modulesPage.lessons')}</h3>
                {moduleLessons.map(lesson => (
                  <button 
                    key={lesson.slug}
                    onClick={() => !isLocked && navigate(`lesson-${lesson.slug}`, lesson.slug)}
                    disabled={isLocked}
                    className={`w-full text-left p-4 rounded-2xl transition-colors flex justify-between items-center ${isLocked ? 'bg-stone-100 cursor-not-allowed' : 'bg-white hover:bg-stone-50'}`}
                  >
                    <div>
                        <p className="font-semibold text-stone-800">{lesson.title}</p>
                        <p className="text-sm text-stone-500">{lesson.summary}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-stone-500">{lesson.duration_min} {t('modulesPage.minutes')}</span>
                        {isLocked ? (
                            <LockIcon />
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-stone-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModulesPage;